import { motion } from "framer-motion";
import { BadgeCheck, Star } from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];

export default function ReviewsSection({ content }) {
  return (
    <section data-testid="reviews-section" className="py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 36 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: EASE }}
          className="flex flex-wrap items-end justify-between gap-8"
        >
          <div className="max-w-2xl">
            <p className="kicker text-navy">
              <span className="h-px w-10 bg-sky" />
              {content.eyebrow}
            </p>
            <h2 className="mt-5 font-display text-3xl font-semibold leading-[1.08] tracking-tight text-navy sm:text-4xl lg:text-5xl">
              {content.title}
            </h2>
            <p className="mt-5 text-base leading-relaxed text-mute sm:text-lg">{content.sub}</p>
          </div>
          <div data-testid="reviews-trust-bar" className="flex items-center gap-4 rounded-full bg-white px-6 py-4 ring-1 ring-navy/10">
            <span className="flex gap-0.5">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} className="h-4 w-4 text-sky" fill="currentColor" strokeWidth={0} />
              ))}
            </span>
            <span className="h-5 w-px bg-navy/10" />
            <span className="text-sm font-extrabold tracking-tight text-navy">Airbnb</span>
            <span className="text-sm font-extrabold tracking-tight text-mute">Booking.com</span>
          </div>
        </motion.div>

        <div className="mt-14 grid items-start gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {content.items.map((r, i) => (
            <motion.figure
              key={r.name}
              data-testid={`review-card-${i}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.85, delay: (i % 3) * 0.1, ease: EASE }}
              className="flex h-full flex-col rounded-[1.75rem] bg-white p-8 ring-1 ring-navy/10 transition-[transform,box-shadow] duration-500 hover:-translate-y-2 hover:shadow-[0_30px_60px_-25px_rgba(15,43,58,0.3)]"
            >
              <div className="flex items-center justify-between">
                <span className="flex gap-0.5" aria-label="5 su 5">
                  {Array.from({ length: 5 }).map((_, s) => (
                    <Star key={s} className="h-4 w-4 text-sky" fill="currentColor" strokeWidth={0} />
                  ))}
                </span>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-sand px-3 py-1 text-[10px] font-extrabold uppercase tracking-[0.12em] text-navy">
                  <BadgeCheck className="h-3.5 w-3.5 text-med" />
                  {content.verified}
                </span>
              </div>
              <blockquote className="mt-5 flex-1 text-[15px] leading-relaxed text-ink">“{r.quote}”</blockquote>
              <figcaption className="mt-7 flex items-center gap-4 border-t border-navy/10 pt-6">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-navy font-display text-lg font-semibold text-sky">
                  {r.name[0]}
                </span>
                <span>
                  <span className="block text-sm font-extrabold text-navy">{r.name}</span>
                  <span className="block text-xs font-semibold text-mute">
                    {r.from} · {r.date}
                  </span>
                  {r.property && <span className="mt-1 block text-[11px] font-bold uppercase tracking-wider text-med">{r.property}</span>}
                </span>
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </div>
    </section>
  );
}
